import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import NameListTooltip from 'components/NameListTooltip';
import FieldsDisplay from 'screens/Applications&WorkOrders/Components/RowItem/Components/FieldsDisplay';
import OpenInNewIcon from 'assets/open_in_new.svg?react';
import formatNumber, { formatCurrency } from 'helpers/formatNumber';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import './index.css';

/**
 * Cells shared by every Linked Records table. Each entry point supplies its own
 * columns and its own per-type wiring (icon, labels, routes); what lives here is
 * the rendering the design specifies identically for all of them.
 */

export interface NamedEntity {
  id: string;
  name: string;
}

export interface FieldsBrief {
  firstFields?: NamedEntity[];
  firstFieldInfo?: {
    id?: string;
    name?: string;
    geometry?: any;
  };
  totalFields?: number;
}

export interface ProductsBrief {
  firstProducts?: NamedEntity[];
  totalProducts?: number;
}

const TOOLTIP_OVERLAY_STYLE = { backgroundColor: '#232630' };

/**
 * Sets `isTruncated` from the element's own overflow, so the tooltip only
 * carries a title when the name is actually clipped. These columns are
 * proportional and have no drag handles, so a window `resize` is the only thing
 * that changes a cell's width.
 */
const useIsOverflowing = (dependency: string) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const measure = () =>
      setIsTruncated(element.scrollWidth > element.clientWidth);

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [dependency]);

  return { ref, isTruncated };
};

interface NameWithCountCellProps {
  tooltipTitle: string;
  names: NamedEntity[];
  total: number;
  maxItems: number;
  secondaryText?: string;
}

/**
 * Primary name plus a "+N" count, which sits outside the truncated name so it
 * stays visible. CSS truncation rather than a JS truncator: in a content-sized
 * flex item a measuring truncator reads its own output and clips early.
 */
export const NameWithCountCell = ({
  tooltipTitle,
  names,
  total,
  maxItems,
  secondaryText
}: NameWithCountCellProps) => {
  const primaryName = names[0]?.name ?? '';
  const { ref, isTruncated } = useIsOverflowing(primaryName);

  return (
    <div className="flex flex-col min-w-0">
      {/* gap-1 = 4px, the gap between name and "+N" in every row of the design.
          The chip's padding and negative margin cancel for layout, so the flex
          gap is the visible gap. */}
      <div className="flex items-center gap-1 min-w-0">
        {/* Outer span is the tooltip's trigger node; the inner one carries the
            ref, so antd's own ref on the child isn't displaced. */}
        <span className="min-w-0 shrink">
          <Tooltip
            title={isTruncated ? primaryName : ''}
            placement="top"
            overlayInnerStyle={TOOLTIP_OVERLAY_STYLE}
          >
            <span
              ref={ref}
              className="block truncate break-all text-sm text-gray-900"
            >
              {primaryName}
            </span>
          </Tooltip>
        </span>
        <NameListTooltip
          title={tooltipTitle}
          names={names}
          total={total}
          maxItems={maxItems}
          placement="bottomRight"
          nameWrap="words"
        />
      </div>
      <span className="text-xs text-neutral-50">{secondaryText}</span>
    </div>
  );
};

interface RecordTypeIconCellProps {
  icon: React.ReactNode;
  /** Hover label, e.g. `Work Order`. */
  title: string;
  testId: string;
  /**
   * Hover fill for the badge, a lighter shade of its own base colour. Each
   * record type has its own, so the badge keeps its identity on hover rather
   * than every type resolving to the same one.
   */
  hoverFill?: string;
}

// Matches the transform transition on .linked-record-type-icon__lift; the
// tooltip anchors to the icon, so opening mid-lift makes it jump.
const ICON_LIFT_MS = 200;

export const RecordTypeIconCell = ({
  icon,
  title,
  testId,
  hoverFill
}: RecordTypeIconCellProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  useEffect(() => {
    if (!isHovered) {
      setIsTooltipOpen(false);
      return undefined;
    }
    const timer = setTimeout(() => setIsTooltipOpen(true), ICON_LIFT_MS);
    return () => clearTimeout(timer);
  }, [isHovered]);

  return (
    <div
      className="linked-record-type-icon flex items-center justify-center"
      style={
        hoverFill
          ? ({
              '--record-type-hover-fill': hoverFill
            } as React.CSSProperties)
          : undefined
      }
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Tooltip
        title={title}
        placement="top"
        open={isTooltipOpen}
        overlayInnerStyle={TOOLTIP_OVERLAY_STYLE}
      >
        <span
          className="linked-record-type-icon__lift flex items-center"
          data-testid={testId}
        >
          {icon}
        </span>
      </Tooltip>
    </div>
  );
};

/** Structural, so each entry point can pass its own record type unchanged. */
export interface AppliedAreaRecord {
  fieldsBrief?: FieldsBrief;
  totalAppliedArea?: number;
  totalAppliedAreaUnit?: string;
}

interface AppliedAreaCellProps {
  record: AppliedAreaRecord;
  maxItems: number;
}

export const AppliedAreaCell = ({ record, maxItems }: AppliedAreaCellProps) => {
  const { fieldsBrief, totalAppliedArea, totalAppliedAreaUnit } = record;
  const names = fieldsBrief?.firstFields ?? [];
  const total = fieldsBrief?.totalFields ?? names.length;

  return (
    <div className="flex flex-col min-w-0">
      <FieldsDisplay
        fields={names}
        firstFieldInfo={fieldsBrief?.firstFieldInfo ?? {}}
        totalFields={total}
        maxTooltipItems={maxItems}
        iconWidth={18}
        iconHeight={18}
        iconColor="#868CA2"
        iconBorderColor="#868CA2"
        disablePositionOffset
        tooltipPlacement="bottomRight"
        tooltipNameWrap="words"
        spacing="compact"
      />
      <span className="text-xs text-neutral-50">
        {formatNumber(
          totalAppliedArea ?? 0,
          getAbbreviatedUnit(totalAppliedAreaUnit ?? 'acre') ?? ''
        )}
      </span>
    </div>
  );
};

export interface ProductsRecord {
  productsBrief?: ProductsBrief;
  totalCost?: number;
}

interface ProductsCellProps {
  record: ProductsRecord;
  maxItems: number;
}

export const ProductsCell = ({ record, maxItems }: ProductsCellProps) => {
  const { productsBrief, totalCost } = record;
  const names = productsBrief?.firstProducts ?? [];
  const total = productsBrief?.totalProducts ?? names.length;

  return (
    <NameWithCountCell
      tooltipTitle="Products"
      names={names}
      total={total}
      maxItems={maxItems}
      secondaryText={formatCurrency(totalCost ?? 0)}
    />
  );
};

interface ViewMoreCellProps {
  onClick: () => void;
  testId: string;
}

export const ViewMoreCell = ({ onClick, testId }: ViewMoreCellProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="flex justify-center items-center">
      <button
        type="button"
        /* `focus:shadow-outline` replaces the removed native ring rather than
           leaving keyboard users with no focus indicator at all — same pairing
           the "+N" chip uses. No `tabIndex`: a button is focusable already. */
        className={`flex flex-col cursor-pointer py-10px px-8px rounded focus:outline-hidden focus:shadow-outline ${
          isHovered ? 'bg-blue-10' : ''
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onClick}
        data-testid={testId}
      >
        <div className="flex gap-1 justify-center whitespace-nowrap">
          <OpenInNewIcon
            className={isHovered ? 'text-blue-50' : 'text-neutral-60'}
          />
          <div className={isHovered ? 'text-blue-50' : 'text-dark-grey'}>
            View More
          </div>
        </div>
      </button>
    </div>
  );
};
